import * as React from "react";
import {useEffect, useState} from "react";
import {Button, Col, Container, Row, Image} from "react-bootstrap";
import {Helmet} from "react-helmet";
import API from "../api";
import {useAuth} from "../AuthUser";
import chrisHeadshot from "../assets/img/chris-headshot.jpg";
import chrisIronDark from "../assets/img/chris-iron-dark.jpg";
import chrisReviewDesk from "../assets/img/chris-review-desk.jpg";
import chrisImpact from "../assets/img/chris-impact.jpg";

// Stable code of the hidden "Chris Tyler Exclusive" plan ($997/month). The
// plan is resolved at runtime via GET plans/code/:code, so each environment
// links to its own plan and admins may rename the plan freely. It must stay
// visible=false so it never shows in the /pricing grid; this page is the
// only way in (MGDWD-175).
const EXCLUSIVE_PLAN_CODE = "chris-tyler-exclusive";

// MGDWD-254 removed the Trustpilot branding; MGDWD-269 replaced the quotes
// themselves — the old six came from Trustpilot, whose free plan does not
// allow displaying their reviews at all. These are our own, from what
// students wrote to Chris (same set as the home page).
const testimonials = [
	{
		name: "Mike Mitchell",
		initials: "MM",
		color: "#fce9c8",
		quote: "I played a round of golf this morning and couldn't believe the distance I was getting with my clubs. It was a 60 degree day, slight wind that was helping, 155y par 3. I hit the purest 8i I've ever hit, it flew over the green and rolled out to 185y. So now I have a good problem of figuring out how far all my clubs will go, everything seems to go about 20-30 yards further than it used to and it's all thanks to you!",
	},
	{
		name: "Lee F.",
		initials: "LF",
		color: "#f7d0c4",
		quote: "I practice this drill for a couple hours hitting off my divot board, no balls. Went to the range and put it all together. OMG the compression was amazing. I'm 60 years old and hitting my 52-degree 100 yards. That's awesome for me. So this is the best part, I tried it with my 3 wood, my favorite club in my bag. When I'm hitting it well before this drill, it would carry 170 yards. Now, with your drill, I swing easier, less effort, and carried the ball 252 yards, absolutely crushing it with no effort.",
	},
	{
		name: "Nick Bastiao",
		initials: "NB",
		color: "#d4e7f6",
		quote: "The ball striking is flipping incredible. The speed and trajectory of my ball flight is awesome! I can't believe how comfortable I feel! I feel so natural with this move. Best of all, ZERO BODY PAIN! Chris, I feel like you have awakened a caged animal in my golf life now. I'm so excited.",
	},
	{
		name: "Tom N.",
		initials: "TN",
		color: "#cfe8d9",
		quote: "I'm 76. If only we had your instruction 50+ years ago! Would have saved: a TON of money on golf lessons. A TON of time at the driving range pounding out buckets (and buckets) of balls. A TON of frustration trying to make bad swing habits into a good golf game.",
	},
	{
		name: "Curt Anderson",
		initials: "CA",
		color: "#e2d8f3",
		quote: "What a great initial lesson! This is exactly the type of feedback I am looking for. Really enjoy your style of teaching as you make no bones about it: making neurological firing adjustments (i.e., a \"swing change\") is not easy and can be tedious. Your acknowledgment of how difficult it is, and the need to teach your nervous system what to do through numerous repetitions, made me choose you as the best for me. Your core audience is people like me who live for that kind of teaching.",
	},
	{
		name: "Reynaldo M.",
		initials: "RM",
		color: "#f6d6e4",
		quote: "You are the best teacher I've ever had and I'd like to thank you for all your hard work on my behalf. When I reviewed my initial swing submissions to my most recent ones, it's incredible the difference even my untrained eye can see.",
	},
];

function Stars({size = 18}) {
	return (
		<span className="rv-stars" style={{"--rv-size": `${size}px`}} aria-label="5 out of 5 stars">
			{[0, 1, 2, 3, 4].map((i) => (
				<i key={i} className="bi bi-star-fill" />
			))}
		</span>
	);
}

function TestimonialCard({quote, name, initials, color}) {
	return (
		<div className="rv-card h-100">
			<div className="d-flex align-items-center gap-2 mb-2">
				<span className="rv-avatar" style={{backgroundColor: color}}>{initials}</span>
				<span className="fw-semibold">{name}</span>
			</div>
			<div className="d-flex align-items-center gap-2 mb-3">
				<Stars size={18} />
			</div>
			<p className="rv-quote mb-0">{quote}</p>
		</div>
	);
}

// Same routing logic as components/PricingButton, but styled with the brand
// orange (variant="secondary") instead of the pricing grid's yellow warning
// buttons, so the CTA matches the rest of this page's accents.
function GetStartedButton({plan}) {
	const auth = useAuth();

	// Until the plan resolves (or if the code is missing in this environment)
	// keep the CTA visible but inert instead of linking to a dead checkout.
	if (!plan?.sourceId) {
		return (
			<div className="d-inline-block pricing-button-lg">
				<Button variant="secondary" className="text-white" disabled>Get Started</Button>
			</div>
		);
	}

	const href = `/plans/${plan.sourceId}`;

	if (auth?.user?.planId === plan.sourceId) {
		return (
			<div className="d-inline-block pricing-button-lg">
				<Button variant="light" disabled>Current Plan</Button>
			</div>
		);
	}
	return (
		<div className="d-inline-block pricing-button-lg">
			<Button
				href={auth?.user ? href : `/signup?r=${href}`}
				variant="secondary"
				className="text-white"
			>
				{auth?.user ? "Upgrade" : "Get Started"}
			</Button>
		</div>
	);
}

function WorkWithChris() {
	const [plan, setPlan] = useState(null);

	useEffect(() => {
		API.get(`plans/code/${EXCLUSIVE_PLAN_CODE}`)
			.then((response) => setPlan(response.data))
			.catch(() => setPlan(null));
	}, []);

	const price = (plan?.amount ?? 99700) / 100;

	return (
		<>
			<Helmet>
				<title>Work Directly With Chris Tyler | MyGolfDNA.com</title>
				<meta
					name="description"
					content="The Chris Tyler Exclusive Membership: every swing you submit is personally reviewed by Chris Tyler."
				/>
			</Helmet>
			<style>{`
				#work-with-chris .pricing-button-lg .btn {
					padding: 0.85rem 3rem;
					font-size: 1.1rem;
					font-weight: 700;
					letter-spacing: 0.5px;
					text-transform: uppercase;
				}
				#wwc-hero {
					background:
						linear-gradient(90deg, rgba(16, 23, 31, 0.96) 0%, rgba(16, 23, 31, 0.88) 38%, rgba(16, 23, 31, 0.35) 78%, rgba(16, 23, 31, 0.45) 100%),
						url(${chrisHeadshot}) right center / cover no-repeat;
				}
				@media (max-width: 991px) {
					#wwc-hero {
						background:
							linear-gradient(rgba(16, 23, 31, 0.9), rgba(16, 23, 31, 0.9)),
							url(${chrisHeadshot}) right center / cover no-repeat;
					}
				}
				@media (max-width: 767px) {
					/* 24px container padding keeps a consistent edge on phones and
					   absorbs the -24px row margins of g-5 rows (otherwise they
					   overflow the default 12px padding → horizontal scroll). */
					#work-with-chris .container {
						padding-left: 24px;
						padding-right: 24px;
					}
				}
				#work-with-chris .wwc-overline {
					color: var(--orange, #ff5b25);
					font-weight: 700;
					letter-spacing: 3px;
					font-size: 0.8rem;
					text-transform: uppercase;
				}
				#work-with-chris h1 {
					font-size: clamp(1.7rem, 4vw, 2.9rem);
					line-height: 1.15;
					letter-spacing: 1px;
				}
				#work-with-chris h2 {
					font-size: clamp(1.3rem, 2.6vw, 1.9rem);
					letter-spacing: 1px;
				}
				#work-with-chris .wwc-statement {
					font-size: clamp(1.15rem, 2.4vw, 1.7rem);
					letter-spacing: 1px;
				}
				#work-with-chris .wwc-stat-number {
					font-size: clamp(1.8rem, 3.6vw, 2.8rem);
					letter-spacing: 1px;
				}
				#work-with-chris .wwc-stat-label {
					letter-spacing: 2px;
					font-size: 0.78rem;
					text-transform: uppercase;
				}
				#work-with-chris .rv-stars {
					display: inline-flex;
					gap: 1px;
					color: #fbbc04;
					font-size: var(--rv-size, 18px);
					line-height: 1;
				}
				#work-with-chris .rv-card {
					background: #fff;
					border: 1px solid #e7e9ee;
					border-radius: 12px;
					padding: 24px;
					box-shadow: 0 1px 2px rgba(16, 23, 31, 0.04);
				}
				#work-with-chris .rv-avatar {
					width: 40px;
					height: 40px;
					border-radius: 50%;
					display: inline-flex;
					align-items: center;
					justify-content: center;
					font-weight: 700;
					font-size: 0.85rem;
					color: #5b4636;
					flex-shrink: 0;
				}
				#work-with-chris .rv-quote {
					font-size: 0.93rem;
					color: #3d434b;
				}
				#work-with-chris .wwc-photo {
					border-radius: 16px;
					box-shadow: 0 24px 48px -24px rgba(16, 23, 31, 0.5);
				}
				#work-with-chris .wwc-check li {
					padding: 6px 0;
					display: flex;
					align-items: flex-start;
					gap: 8px;
				}
				#work-with-chris .wwc-guarantee {
					border-left: 4px solid var(--orange, #ff5b25);
					background: #fff;
					border-radius: 0 12px 12px 0;
					box-shadow: 0 1px 2px rgba(16, 23, 31, 0.06);
				}
			`}</style>
			<div id="work-with-chris">
				{/* Hero */}
				<div id="wwc-hero" style={{padding: "96px 0 88px"}}>
					<Container>
						<Row>
							<Col lg={7}>
								<div className="wwc-overline mb-3">The Chris Tyler Exclusive Membership</div>
								<h1 className="monument-font-black text-white text-uppercase">
									Work directly with Chris Tyler,{" "}
									<span className="text-secondary">and only Chris Tyler</span>
								</h1>
								<p className="text-white mt-4 mb-2" style={{fontWeight: 300, fontSize: 20}}>
									Every Swing. Every Review. Every Step of Your Improvement.
								</p>
								<p className="text-white-50 mb-4" style={{maxWidth: 560}}>
									For golfers who want the highest level of coaching: every swing you
									submit is personally reviewed by Chris Tyler — one of the most
									experienced remote golf instructors in the world.
								</p>
								<GetStartedButton plan={plan} />
								<div className="text-white-50 small mt-3">
									${price}/month · Limited availability
								</div>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Statement strip */}
				<div style={{backgroundColor: "#fff", padding: "56px 0"}}>
					<Container>
						<Row className="justify-content-center text-center">
							<Col lg={9}>
								<div className="monument-font-black text-primary text-uppercase wwc-statement">
									No handoffs. No inconsistency.{" "}
									<span className="text-secondary">No compromises.</span>
								</div>
								<p className="text-black-50 mt-3 mb-0" style={{fontSize: 18, fontWeight: 300}}>
									Just one coach who knows your swing as well as you do.
								</p>
							</Col>
						</Row>
					</Container>
				</div>

				{/* One coach — text + driver photo */}
				<div style={{backgroundColor: "#fafbfd", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Your golf swing deserves <span className="text-secondary">one coach</span>
								</h2>
							</Col>
						</Row>
						<Row className="align-items-center g-5">
							<Col lg={6}>
								<p>One of the biggest problems with traditional instruction is inconsistency.</p>
								<p>
									Different instructors notice different things. They explain concepts
									differently. They prioritize different changes. That can leave golfers
									feeling confused, overwhelmed, and constantly starting over.
								</p>
								<p>
									With the Chris Tyler Exclusive Membership, you&apos;ll build a coaching
									relationship with one instructor from your very first swing. Chris
									learns your tendencies, understands your goals, remembers what
									you&apos;ve worked on, and knows exactly what your next step should be.
								</p>
								<p className="mb-0">
									Constantly dialing in your program to match you perfectly, instead of
									repeating your story every lesson, you&apos;ll simply keep moving forward.
								</p>
							</Col>
							<Col lg={6}>
								<Image src={chrisIronDark} alt="Chris Tyler on the course" className="wwc-photo" fluid />
							</Col>
						</Row>
					</Container>
				</div>

				{/* Stats band */}
				<div style={{backgroundColor: "#1B2631", padding: "64px 0"}}>
					<Container>
						<Row className="text-center gy-4">
							<Col md={4}>
								<div className="monument-font-black text-secondary wwc-stat-number">120,000+</div>
								<div className="text-white-50 wwc-stat-label mt-2">Swing reviews completed</div>
							</Col>
							<Col md={4}>
								<div className="monument-font-black text-secondary wwc-stat-number">15,000+</div>
								<div className="text-white-50 wwc-stat-label mt-2">Hours of golf instruction</div>
							</Col>
							<Col md={4}>
								<div className="monument-font-black text-secondary wwc-stat-number">PGA &amp; LPGA</div>
								<div className="text-white-50 wwc-stat-label mt-2">Tour pros coached</div>
							</Col>
						</Row>
						<Row className="justify-content-center mt-5">
							<Col lg={8} className="text-center">
								<p className="text-white-50 mb-0" style={{fontWeight: 300}}>
									Master Instructor Certified · Director of Instruction · Extensive
									experience coaching golfers remotely using modern video analysis and
									motor-learning principles. Every review includes the same detailed
									analysis, personalized drills, and actionable feedback that has helped
									thousands of golfers make lasting improvements.
								</p>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Everything included */}
				<div style={{backgroundColor: "#fff", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Everything in our Elite tier — plus{" "}
									<span className="text-secondary">one important difference</span>
								</h2>
							</Col>
						</Row>
						<Row className="align-items-center g-5">
							<Col lg={6} className="order-lg-first order-last">
								{/* Chris at his review station — the literal picture of the
								    "completed by Chris Tyler personally" guarantee. */}
								<Image src={chrisReviewDesk} alt="Chris Tyler reviewing member swings" className="wwc-photo" fluid />
							</Col>
							<Col lg={6}>
								<ul className="list-unstyled wwc-check mb-0">
									<li><i className="bi bi-check-circle text-success" /> Unlimited swing reviews</li>
									<li><i className="bi bi-check-circle text-success" /> Priority turnaround</li>
									<li><i className="bi bi-check-circle text-success" /> Personalized drills</li>
									<li><i className="bi bi-check-circle text-success" /> Side-by-side video analysis</li>
									<li><i className="bi bi-check-circle text-success" /> Progress tracking</li>
									<li><i className="bi bi-check-circle text-success" /> Direct messaging</li>
									<li><i className="bi bi-check-circle text-success" /> Ongoing coaching and accountability</li>
								</ul>
							</Col>
						</Row>
						<Row className="mt-4">
							<Col>
								<div className="wwc-guarantee p-4">
									<div className="fw-semibold mb-1">And one exclusive guarantee:</div>
									<div className="fw-bold" style={{fontSize: 18}}>
										Every swing review is completed by Chris Tyler personally.
									</div>
									<div className="text-black-50 mt-1">No substitutions. Ever.</div>
								</div>
							</Col>
						</Row>
					</Container>
				</div>

				{/* Who it's for + availability */}
				<div style={{backgroundColor: "#fafbfd", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-4">
							<Col lg={10}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Who this membership is for
								</h2>
							</Col>
						</Row>
						<Row className="align-items-center g-5">
							<Col lg={6}>
								<ul className="list-unstyled wwc-check mb-4">
									<li><i className="bi bi-check-circle text-success" /> You want maximum consistency in your coaching</li>
									<li><i className="bi bi-check-circle text-success" /> You value personalized instruction over generic advice</li>
									<li><i className="bi bi-check-circle text-success" /> You want guidance from Chris himself throughout your entire journey</li>
								</ul>
								<p>
									If you&apos;re serious about improving your golf game, there&apos;s simply
									no substitute for having one coach who understands every detail of your
									swing.
								</p>
								<p className="mb-0">
									<b>Limited availability.</b> Because every swing review is completed
									personally by Chris, membership is intentionally limited. When coaching
									capacity is full, new members will be placed on a waiting list until
									space becomes available.
								</p>
							</Col>
							<Col lg={6}>
								<Image src={chrisImpact} alt="Chris Tyler mid-swing" className="wwc-photo" fluid />
							</Col>
						</Row>
					</Container>
				</div>

				{/* Testimonials — white review cards on the dark brand
				    background so the page alternates dark/white all the way to
				    the white final CTA before the dark footer. */}
				<div style={{backgroundColor: "#1B2631", padding: "72px 0"}}>
					<Container>
						<Row className="justify-content-center text-center mb-2">
							<Col lg={8}>
								<h2 className="monument-font-black text-white text-uppercase">
									What golfers <span className="text-secondary">say</span>
								</h2>
							</Col>
						</Row>
						<Row className="g-4">
							{testimonials.map((t) => (
								<Col lg={4} md={6} key={t.name}>
									<TestimonialCard {...t} />
								</Col>
							))}
						</Row>
					</Container>
				</div>

				{/* Final CTA on white: the last section before the dark footer
				    stays light so the footer's own top gap reads as intentional
				    white space instead of a stray strip. */}
				<div style={{backgroundColor: "#fff", padding: "80px 0"}}>
					<Container>
						<Row className="justify-content-center text-center">
							<Col lg={8}>
								<h2 className="monument-font-black text-primary text-uppercase">
									Your coach. Your swing. <span className="text-secondary">Your progress.</span>
								</h2>
								<p className="text-black-50 mt-3" style={{fontWeight: 300, fontSize: 18}}>
									If you&apos;re ready to commit to becoming the best golfer you can be,
									there&apos;s no better way than working directly with Chris Tyler every
									step of the way.
								</p>
								<div className="d-flex align-items-end justify-content-center gap-1 mt-4 text-primary">
									<span className="monument-font-black" style={{fontSize: "3rem", lineHeight: 1}}>
										${price}
									</span>
									<span className="text-black-50 mb-1">/month</span>
								</div>
								<div className="mt-4 mb-4">
									<GetStartedButton plan={plan} />
								</div>
								<Row className="justify-content-center">
									<Col md={10} lg={8} className="text-start">
										<div className="fw-semibold mb-2">Includes:</div>
										<ul className="list-unstyled wwc-check mb-0">
											<li><i className="bi bi-check-circle text-success" /> Unlimited Swing Reviews</li>
											<li><i className="bi bi-check-circle text-success" /> (2) 30 Minute Zoom Lessons Monthly</li>
											<li><i className="bi bi-check-circle text-success" /> Guaranteed fastest turnaround</li>
											<li><i className="bi bi-check-circle text-success" /> Personalized improvement roadmap and accountability plan</li>
											<li><i className="bi bi-check-circle text-success" /> Access to the full video library</li>
											<li><i className="bi bi-check-circle text-success" /> Personal cell phone access</li>
										</ul>
									</Col>
								</Row>
							</Col>
						</Row>
					</Container>
				</div>
			</div>
		</>
	);
}

export default WorkWithChris;
